import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  fetchOnboardingRuns,
  fetchRunsSummary,
  reinvokeRun,
  deleteRun,
  getRecruiterRunView,
  fetchFlows,
  initiateOnboarding,
  requestRunChanges,
  completeRunReview,
} from '../../../Redux/ActionCreators/onboardingAction';
import '../onboarding.scss';
import { Table } from "react-bootstrap";
import { useAppPermission } from '../../../hooks/useAppPermission';
import { P } from '../../../constants/rbac';

const statusConfig = {
  invited: { cls: 'ob-badge--purple', label: 'Invited' },
  pending: { cls: 'ob-badge--amber', label: 'Pending' },
  in_progress: { cls: 'ob-badge--blue', label: 'In Progress' },
  in_review: { cls: 'ob-badge--orange', label: 'In Review' },
  changes_requested: { cls: 'ob-badge--red', label: 'Changes Requested' },
  completed: { cls: 'ob-badge--green', label: 'Completed' },
  cancelled: { cls: 'ob-badge--gray', label: 'Cancelled' },
  expired: { cls: 'ob-badge--red', label: 'Expired' },
};

const RUN_SUMMARY_COLORS = {
  completed:         { border: '#16a34a', color: '#16a34a', bg: 'rgba(22,163,74,0.08)' },
  invited:           { border: '#d97706', color: '#b45309', bg: 'rgba(217,119,6,0.08)' },
  pending:           { border: '#d97706', color: '#b45309', bg: 'rgba(217,119,6,0.08)' },
  changes_requested: { border: '#d97706', color: '#b45309', bg: 'rgba(217,119,6,0.08)' },
  in_progress:       { border: '#2563eb', color: '#2563eb', bg: 'rgba(37,99,235,0.08)' },
  in_review:         { border: '#7c3aed', color: '#7c3aed', bg: 'rgba(124,58,237,0.08)' },
  cancelled:         { border: '#e4e9f0', color: '#8a9199', bg: '#f4f5f7' },
  expired:           { border: '#e4e9f0', color: '#8a9199', bg: '#f4f5f7' },
};

const RunSummaryStrip = ({ summary, activeStatus, onStatusClick, onClear }) => {
  if (!summary?.statuses?.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, margin: '0 0 12px', padding: '10px 14px', border: '1px solid #e4e9f0', borderRadius: 10, background: '#fff' }}>
      <span
        onClick={onClear}
        style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 999, cursor: 'pointer', border: `1px solid ${!activeStatus ? '#f75891' : '#e4e9f0'}`, background: !activeStatus ? '#f75891' : '#f4f5f7', color: !activeStatus ? '#fff' : '#1a1a2e' }}
      >
        All <strong>{summary.total_count ?? 0}</strong>
      </span>
      {summary.statuses.map(({ status, label, count }) => {
        const isActive = activeStatus === status;
        const colors = RUN_SUMMARY_COLORS[status] || RUN_SUMMARY_COLORS.cancelled;
        const isEmpty = (count ?? 0) === 0;
        return (
          <span
            key={status}
            onClick={() => isActive ? onClear() : onStatusClick(status)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 12, padding: '4px 10px', borderRadius: 999,
              border: `1px solid ${isActive ? colors.border : '#e4e9f0'}`,
              background: isActive ? colors.bg : '#f4f5f7',
              color: isActive ? colors.color : isEmpty ? '#9aa3b0' : '#525b66',
              cursor: 'pointer', whiteSpace: 'nowrap',
              opacity: isEmpty && !isActive ? 0.55 : 1,
            }}
          >
            {label}
            <strong style={{ color: isActive ? colors.color : isEmpty ? '#9aa3b0' : '#1a1a2e', fontWeight: 700 }}>{count ?? 0}</strong>
          </span>
        );
      })}
      {activeStatus && (
        <button onClick={onClear} style={{ marginLeft: 'auto', fontSize: 11.5, color: '#8a9199', background: 'none', border: '1px solid transparent', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>
          ✕ Clear filter
        </button>
      )}
    </div>
  );
};

const renderCustomPagination = (totalPages, currentPage, setCurrentPage) => {
  const pages = [];
  pages.push(
    <button className="prev-btn" key="prev" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
      <img src="/images/leftarrow-icon.svg" alt="" />
    </button>
  );
  pages.push(
    <button key={1} className={currentPage === 1 ? 'active' : ''} onClick={() => setCurrentPage(1)}>{1}</button>
  );
  if (currentPage > 3) pages.push(<button key="left-dots">...</button>);
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);
  for (let i = start; i <= end; i++) {
    pages.push(<button key={i} className={currentPage === i ? 'active' : ''} onClick={() => setCurrentPage(i)}>{i}</button>);
  }
  if (currentPage < totalPages - 2) pages.push(<button key="right-dots">...</button>);
  if (totalPages > 1) {
    pages.push(
      <button key={totalPages} className={currentPage === totalPages ? 'active' : ''} onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
    );
  }
  pages.push(
    <button className="next-btn" key="next" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>
      <img src="/images/leftarrow-icon.svg" alt="" style={{ rotate: "180deg" }} />
    </button>
  );
  return pages;
};

const ActiveOnboarding = () => {
  const dispatch = useDispatch();
  const { can } = useAppPermission();
  const canEdit = can(P.ONBOARDING_FLOW_EDIT);
  const canRead = can(P.ONBOARDING_FLOW_READ);
  const showActions = canRead || canEdit;
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [flowFilter, setFlowFilter] = useState('');
  const [flowSearch, setFlowSearch] = useState('');
  const [showFlowDropdown, setShowFlowDropdown] = useState(false);
  const flowDropdownRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, page: 1, page_size: 10 });

  // Recruiter view modal
  const [viewModal, setViewModal] = useState({ open: false, runId: null, data: null, loading: false });
  const [expandedForms, setExpandedForms] = useState({});
  const [commentModal, setCommentModal] = useState({ open: false, runId: null, value: '' });

  const toggleForm = (formId) => setExpandedForms(prev => ({ ...prev, [formId]: !prev[formId] }));

  const [runsSummary, setRunsSummary] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);
  const [flows, setFlows] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [resendingId, setResendingId] = useState(null);
  const [endConfirmId, setEndConfirmId] = useState(null);
  const [endReason, setEndReason] = useState('');
  const [ending, setEnding] = useState(false);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (flowDropdownRef.current && !flowDropdownRef.current.contains(e.target)) {
        setShowFlowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showSnackbar = (message) => {
    setSnackbar({ open: true, message });
    setTimeout(() => setSnackbar({ open: false, message: '' }), 3000);
  };

  const loadRuns = useCallback(async (page, size, status, searchTerm = '', flowId = '') => {
    setLoading(true);
    const paramParts = [];
    if (status) paramParts.push(`status=${status}`);
    if (searchTerm) paramParts.push(`search=${encodeURIComponent(searchTerm)}`);
    if (flowId) paramParts.push(`flow_id=${flowId}`);
    const params = paramParts.join('&');
    const summaryParams = flowId ? `flow_id=${flowId}` : '';
    const [res, summaryRes] = await Promise.all([
      dispatch(fetchOnboardingRuns(page, size, params)),
      dispatch(fetchRunsSummary(summaryParams)),
    ]);
    if (res) {
      setRuns(res.data || []);
      setPagination(res.pagination || { total: (res.data || []).length, page, page_size: size });
    }
    if (summaryRes) {
      setRunsSummary(summaryRes?.data || summaryRes);
    }
    setLoading(false);
  }, [dispatch]);

  useEffect(() => { loadRuns(currentPage, pageSize, statusFilter, debouncedSearch, flowFilter); }, [loadRuns, currentPage, pageSize, statusFilter, debouncedSearch, flowFilter]);

  useEffect(() => {
    dispatch(fetchFlows(1, 100, 'status=active')).then(res => {
      setFlows(res?.data || []);
    });
  }, [dispatch]);

  const handleViewRun = async (runId) => {
    setViewModal({ open: true, runId, data: null, loading: true });
    const res = await dispatch(getRecruiterRunView(runId));
    setViewModal({ open: true, runId, data: res?.data || res || null, loading: false });
  };

  const handleRequestChanges = (runId) => {
    setCommentModal({ open: true, runId, value: '' });
  };

  const handleSubmitRequestChanges = async () => {
    const { runId, value } = commentModal;
    if (!value.trim()) { showSnackbar('Please enter a message for the candidate.'); return; }
    setCommentModal(m => ({ ...m, open: false }));
    setViewModal(m => ({ ...m, loading: true }));
    await dispatch(requestRunChanges(runId, { comment: value.trim(), send_email: true }));
    await loadRuns(currentPage, pageSize, statusFilter, debouncedSearch, flowFilter);
    const res = await dispatch(getRecruiterRunView(runId));
    setViewModal(m => ({ ...m, data: res?.data || res || null, loading: false }));
    showSnackbar('Change request sent to the candidate.');
  };

  const handleCompleteReview = async (runId) => {
    setViewModal(m => ({ ...m, loading: true }));
    await dispatch(completeRunReview(runId));
    await loadRuns(currentPage, pageSize, statusFilter, debouncedSearch, flowFilter);
    setViewModal({ open: false, runId: null, data: null, loading: false });
    showSnackbar('Onboarding marked as completed!');
  };

  const handleResend = async (runId) => {
    setResendingId(runId);
    await dispatch(reinvokeRun(runId));
    setResendingId(null);
    showSnackbar('onboarding invite resent to the candidate.');
  };

  const handleEnd = async () => {
    setEnding(true);
    const payload = endReason.trim() ? { reason: endReason.trim() } : undefined;
    await dispatch(deleteRun(endConfirmId, payload));
    setEndConfirmId(null);
    setEndReason('');
    setEnding(false);
    await loadRuns(currentPage, pageSize, statusFilter, debouncedSearch, flowFilter);
    showSnackbar('Onboarding run ended.');
  };

  const toStr = (val) => (val && typeof val === 'object' ? '' : val ?? '');

  const filtered = runs;

  const totalPages = Math.ceil((pagination.total || 0) / pageSize);

  const stats = {
    total: pagination.total || 0,
    inProgress: runs.filter(r => r.status === 'in_progress').length,
    inReview: runs.filter(r => r.status === 'in_review').length,
    changesRequested: runs.filter(r => r.status === 'changes_requested').length,
    completed: runs.filter(r => r.status === 'completed').length,
    pending: runs.filter(r => r.status === 'pending').length,
  };

  return (
    <div className="ob-page">
      <div className="ob-page__header">
        <div className="ob-page__header-left">
          <h2>Active Onboarding</h2>
          <p>Track onboarding progress across all candidates</p>
        </div>
        <button className="ob-btn" onClick={() => {
          clearTimeout(debounceRef.current);
          setSearch('');
          setDebouncedSearch('');
          setStatusFilter('');
          setFlowFilter('');
          setFlowSearch('');
          setCurrentPage(1);
          loadRuns(1, pageSize, '', '', '');
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* ── Search & Filter ─────────────────────────────── */}
      <div className="ob-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <input
            style={{ flex: 1, minWidth: 200, padding: '9px 12px', border: '1px solid #e4e9f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
            placeholder="Search by candidate name or email…"
            value={search}
            onChange={e => {
              const val = e.target.value;
              setSearch(val);
              clearTimeout(debounceRef.current);
              debounceRef.current = setTimeout(() => {
                setDebouncedSearch(val);
                setCurrentPage(1);
              }, 400);
            }}
          />
          <select
            style={{ padding: '9px 12px', border: '1px solid #e4e9f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', background: '#fff' }}
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All statuses</option>
            <option value="invited">Invited</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="in_review">In Review</option>
            <option value="changes_requested">Changes Requested</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
          </select>

          {/* ── Searchable Flow Dropdown ── */}
          <div ref={flowDropdownRef} style={{ position: 'relative', minWidth: 200 }}>
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '9px 12px', border: `1px solid ${flowFilter ? '#f75891' : '#e4e9f0'}`,
                borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer',
                fontFamily: 'inherit',
              }}
              onClick={() => { setShowFlowDropdown(v => !v); setFlowSearch(''); }}
            >
              <span style={{ flex: 1, color: flowFilter ? '#1a1a2e' : '#8a9199', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {flowFilter ? (flows.find(f => f.id === flowFilter)?.name || 'Flow') : 'All flows'}
              </span>
              {flowFilter && (
                <span
                  style={{ fontSize: 11, color: '#8a9199', padding: '0 2px', cursor: 'pointer' }}
                  onClick={e => { e.stopPropagation(); setFlowFilter(''); setFlowSearch(''); setCurrentPage(1); }}
                  title="Clear flow filter"
                >✕</span>
              )}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8a9199" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>

            {showFlowDropdown && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                background: '#fff', border: '1px solid #e4e9f0', borderRadius: 8,
                boxShadow: '0 4px 16px rgba(26,26,46,0.12)', zIndex: 200, overflow: 'hidden',
              }}>
                <div style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0' }}>
                  <input
                    autoFocus
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid #e4e9f0', borderRadius: 6, fontSize: 12, outline: 'none', fontFamily: 'inherit' }}
                    placeholder="Search flows…"
                    value={flowSearch}
                    onChange={e => setFlowSearch(e.target.value)}
                    onClick={e => e.stopPropagation()}
                  />
                </div>
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  <div
                    style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: !flowFilter ? '#f75891' : '#525b66', fontWeight: !flowFilter ? 600 : 400 }}
                    onMouseDown={() => { setFlowFilter(''); setShowFlowDropdown(false); setCurrentPage(1); }}
                  >
                    All flows
                  </div>
                  {flows
                    .filter(f => !flowSearch || f.name?.toLowerCase().includes(flowSearch.toLowerCase()))
                    .map(f => (
                      <div
                        key={f.id}
                        style={{
                          padding: '8px 12px', fontSize: 13, cursor: 'pointer',
                          background: flowFilter === f.id ? '#fdf2f7' : 'transparent',
                          color: flowFilter === f.id ? '#f75891' : '#1a1a2e',
                          fontWeight: flowFilter === f.id ? 600 : 400,
                        }}
                        onMouseDown={() => { setFlowFilter(f.id); setShowFlowDropdown(false); setCurrentPage(1); }}
                      >
                        {f.name}
                      </div>
                    ))
                  }
                  {flows.filter(f => !flowSearch || f.name?.toLowerCase().includes(flowSearch.toLowerCase())).length === 0 && (
                    <div style={{ padding: '10px 12px', fontSize: 12, color: '#8a9199', fontStyle: 'italic' }}>No flows found</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <RunSummaryStrip
        summary={runsSummary}
        activeStatus={statusFilter}
        onStatusClick={(status) => { setStatusFilter(status); setCurrentPage(1); }}
        onClear={() => { setStatusFilter(''); setCurrentPage(1); }}
      />

      {loading ? (
        <div className="ob-spinner"><div className="spinner-border" role="status" /></div>
      ) : filtered.length === 0 ? (
        <div className="ob-empty">
          <span className="ob-empty__icon">🚀</span>
          <div className="ob-empty__title">{search || statusFilter ? 'No matching records' : 'No onboarding runs yet'}</div>
          <div className="ob-empty__sub">
            {search || statusFilter
              ? 'Try adjusting your search or filter.'
              : 'Initiate onboarding from above or from the Candidates page.'}
          </div>
        </div>
      ) : (
        <div className="ob-table-wrap">
          <Table responsive className="ob-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Job</th>
                <th>Status</th>
                <th>Joining Date</th>
                {showActions && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((run, i) => {
                const runId = run.id || run.idx || i;
                const candidateName = toStr(run.candidate_name || run.fullName || run.candidateName) || '—';
                const candidateEmail = toStr(run.email || run.candidateEmail);
                const jobName = toStr(run.job_title) || '—';
                const statusKey = toStr(run.status) || '';
                const sc = statusConfig[statusKey] || statusConfig.pending;
                const joiningDate = toStr(run.joining_date || run.joiningDate);
                return (
                  <tr key={runId}>
                    <td>
                      <div className="td-name">{candidateName}</div>
                      <div className="td-sub">{candidateEmail}</div>
                    </td>
                    <td>{jobName}</td>
                    <td><span className={`ob-badge ${sc.cls}`}>{sc.label}</span></td>
                    <td>{joiningDate ? new Date(joiningDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</td>
                    {showActions && (
                    <td>
                      <div style={{ display: 'flex', gap: 5 }}>
                        {canRead && (
                        <button
                          className="ob-btn ob-btn--sm ob-btn--icon-only"
                          onClick={() => handleViewRun(runId)}
                          title="View candidate progress"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                          </svg>
                        </button>
                        )}
                        {canEdit && ['invited', 'pending', 'in_progress'].includes(statusKey) && (
                          <button
                            className="ob-btn ob-btn--sm ob-btn--icon-only"
                            style={{ color: '#1e40af', borderColor: '#bfdbfe', background: '#eff6ff' }}
                            onClick={() => handleResend(runId)}
                            disabled={resendingId === runId}
                            title="Resend Onboarding invite"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                            </svg>
                          </button>
                        )}
                        {canEdit && !['completed', 'cancelled', 'expired'].includes(statusKey) && (
                          <button
                            className="ob-btn ob-btn--sm ob-btn--icon-only ob-btn--danger"
                            onClick={() => { setEndConfirmId(runId); setEndReason(''); }}
                            disabled={resendingId === runId}
                            title="revoke onboarding"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {!loading && pagination.total > 0 && (
        <div className="pagination d-flex flex-wrap align-items-center justify-content-between">
          <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
            <div className="d-flex flex-wrap" style={{ gap: '20px' }}>
              <div className="pagination-info d-flex align-items-center">
                <span style={{ color: '#525B66' }}>
                  Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, pagination.total)} of <strong>{pagination.total}</strong> Records
                </span>
              </div>
              <div className="pagination-buttons">
                {renderCustomPagination(totalPages, currentPage, setCurrentPage)}
              </div>
            </div>
            <div className="d-flex align-items-center">
              <span style={{ color: '#525B66', lineHeight: '100%' }}>Rows per page</span>&nbsp;&nbsp;&nbsp;&nbsp;
              <select
                value={pageSize}
                onChange={e => { setPageSize(parseInt(e.target.value)); setCurrentPage(1); }}
                style={{ width: '46px', height: '32px', borderRadius: '2px', border: '1px solid #8A9199', color: '#8A9199' }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ── End Confirmation Modal ───────────────────── */}
      {endConfirmId && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(26,26,46,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: 16 }}
          onClick={() => setEndConfirmId(null)}
        >
          <div
            style={{ background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 400, boxShadow: '0 8px 40px rgba(26,26,46,0.18)' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>End Onboarding?</h3>
            <p style={{ fontSize: 13, color: '#525b66', margin: '0 0 14px', lineHeight: 1.55 }}>
              This will permanently end this onboarding run. The candidate will lose access to their onboarding.
            </p>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#525b66', display: 'block', marginBottom: 6 }}>
              Reason <span style={{ fontWeight: 400, color: '#8a9199' }}>(optional)</span>
            </label>
            <textarea
              rows={3}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #e4e9f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: 16 }}
              placeholder="Enter a reason for ending this onboarding…"
              value={endReason}
              onChange={e => setEndReason(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="ob-btn" onClick={() => { setEndConfirmId(null); setEndReason(''); }}>Cancel</button>
              <button
                className="ob-btn ob-btn--primary"
                style={{ background: '#ef4444', borderColor: '#ef4444' }}
                onClick={handleEnd}
                disabled={ending}
              >
                {ending ? 'Ending…' : 'End Onboarding'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Snackbar ──────────────────────────────────── */}
      {snackbar.open && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#1a1a2e', color: '#fff', padding: '10px 20px', borderRadius: 8,
          fontSize: 13, fontWeight: 500, zIndex: 1300, boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
          whiteSpace: 'nowrap',
        }}>
          {snackbar.message}
        </div>
      )}

      {/* ── Request Changes Modal ─────────────────────── */}
      {commentModal.open && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(26,26,46,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: 16 }}
          onClick={() => setCommentModal(m => ({ ...m, open: false }))}
        >
          <div
            style={{ background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 420, boxShadow: '0 8px 40px rgba(26,26,46,0.18)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>Request Changes</h3>
              <img src="/images/close-icon.svg" alt="Close" style={{ cursor: 'pointer', width: 18, height: 18 }} onClick={() => setCommentModal(m => ({ ...m, open: false }))} />
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#525b66', display: 'block', marginBottom: 6 }}>Message to the candidate</label>
            <textarea
              autoFocus
              rows={4}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #e4e9f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
              placeholder="Describe what needs to be corrected…"
              value={commentModal.value}
              onChange={e => setCommentModal(m => ({ ...m, value: e.target.value }))}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button className="ob-btn" style={{ minWidth: 80 }} onClick={() => setCommentModal(m => ({ ...m, open: false }))}>Cancel</button>
              <button className="ob-btn" style={{ minWidth: 80, background: '#f75891', color: '#fff', borderColor: '#f75891' }} onClick={handleSubmitRequestChanges}>Send</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Recruiter Run View Modal ───────────────────── */}
      {viewModal.open && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(26,26,46,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1100, padding: 16,
          }}
          onClick={() => setViewModal({ open: false, runId: null, data: null, loading: false })}
        >
          <div
            className="ob-modal-scroll"
            style={{
              background: '#fff', borderRadius: 14, padding: 28,
              width: '100%', maxWidth: 540, boxShadow: '0 8px 40px rgba(26,26,46,0.18)',
              maxHeight: '80vh', overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>Candidate Progress</h3>
              <img
                src="/images/close-icon.svg"
                alt="Close"
                style={{ cursor: 'pointer', width: 18, height: 18 }}
                onClick={() => setViewModal({ open: false, runId: null, data: null, loading: false })}
              />
            </div>
            {viewModal.loading ? (
              <div className="ob-spinner"><div className="spinner-border" role="status" /></div>
            ) : viewModal.data ? (
              <div>
                <div style={{ padding: '12px 14px', background: '#f4f5f7', borderRadius: 8, marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{viewModal.data.run?.candidate_name || '—'}</div>
                  <div style={{ fontSize: 12, color: '#8a9199', marginTop: 2 }}>{viewModal.data.run?.email}</div>
                  <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#525b66' }}>Flow: <strong>{viewModal.data.flow_name || '—'}</strong></span>
                    <span style={{ fontSize: 12, color: '#525b66' }}>Job: <strong>{viewModal.data.run?.job_title || '—'}</strong></span>
                    <span style={{ fontSize: 12, color: '#525b66' }}>Joining: <strong>{viewModal.data.run?.joining_date ? new Date(viewModal.data.run.joining_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</strong></span>
                  </div>
                </div>
                {(() => {
                  const phases = viewModal.data.phases || [];
                  const completed = phases.filter(p => p.status === 'completed').length;
                  const pct = phases.length ? Math.round((completed / phases.length) * 100) : 0;
                  return (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#525b66', marginBottom: 8 }}>Overall Progress</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ flex: 1, height: 8, borderRadius: 99, background: '#e4e9f0', overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: '#f75891', borderRadius: 99 }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#f75891' }}>{pct}%</span>
                      </div>
                    </div>
                  );
                })()}
                <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 600, color: '#525b66' }}>Phases</div>
                {(viewModal.data.phases || []).map((s, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: s.status === 'completed' ? '#f75891' : s.status === 'in_progress' ? '#3b82f6' : '#e4e9f0', flexShrink: 0,
                    }}>
                      {s.status === 'completed' ? (
                        <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                          <path d="M2 7l3.5 3.5L12 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : (
                        <span style={{ fontSize: 11, color: s.status === 'in_progress' ? '#fff' : '#8a9199', fontWeight: 600 }}>{idx + 1}</span>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>{s.label}</div>
                    </div>
                    <span className={`ob-badge ${s.status === 'completed' ? 'ob-badge--green' : s.status === 'in_progress' ? 'ob-badge--blue' : 'ob-badge--gray'}`}>
                      {s.status === 'completed' ? 'Done' : s.status === 'in_progress' ? 'In Progress' : 'Pending'}
                    </span>
                  </div>
                ))}
                {(viewModal.data.forms || []).length > 0 && (
                  <>
                    <div style={{ marginTop: 16, marginBottom: 8, fontSize: 12, fontWeight: 600, color: '#525b66' }}>Forms</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {viewModal.data.forms.map((f, idx) => {
                        const isOpen = expandedForms[f.form_id] !== false;
                        const responses = f.responses || {};
                        const responseEntries = Object.entries(responses);
                        return (
                          <div key={idx} style={{ border: '1px solid #e4e9f0', borderRadius: 10, overflow: 'hidden' }}>
                            <div
                              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', cursor: 'pointer', background: isOpen ? '#fafbfc' : '#fff' }}
                              onClick={() => toggleForm(f.form_id)}
                            >
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{f.form_name}</div>
                                {f.completed_at && <div style={{ fontSize: 11, color: '#8a9199', marginTop: 2 }}>Completed {new Date(f.completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>}
                              </div>
                              <span className={`ob-badge ${f.status === 'completed' ? 'ob-badge--green' : f.status === 'in_progress' ? 'ob-badge--blue' : 'ob-badge--gray'}`}>
                                {f.status === 'completed' ? 'Done' : f.status === 'in_progress' ? 'In Progress' : 'Not Started'}
                              </span>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8a9199" strokeWidth="2" style={{ flexShrink: 0, transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                                <polyline points="6 9 12 15 18 9" />
                              </svg>
                            </div>
                            {isOpen && (
                              <div style={{ background: '#f8f9fb', padding: '10px 14px 12px', borderTop: '1px solid #eef0f3' }}>
                                {responseEntries.length === 0 ? (
                                  <div style={{ fontSize: 12, color: '#8a9199', fontStyle: 'italic' }}>No responses yet.</div>
                                ) : (
                                  responseEntries.map(([label, value]) => {
                                    const isFile = value && typeof value === 'object' && value.file_url;
                                    return (
                                      <div key={label} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid #eef0f3', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <span style={{ fontSize: 12, color: '#8a9199', minWidth: 140, flexShrink: 0 }}>{label}</span>
                                        {isFile ? (
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span style={{ fontSize: 12, color: '#1a1a2e', fontWeight: 500, wordBreak: 'break-all' }}>{value.file_name}</span>
                                            <a
                                              href={value.file_url}
                                              download={value.file_name}
                                              target="_blank"
                                              rel="noreferrer"
                                              title="Download file"
                                              style={{ color: '#f75891', flexShrink: 0, display: 'flex', alignItems: 'center' }}
                                            >
                                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="7 10 12 15 17 10" />
                                                <line x1="12" y1="15" x2="12" y2="3" />
                                              </svg>
                                            </a>
                                          </div>
                                        ) : (
                                          <span style={{ fontSize: 12, color: '#1a1a2e', fontWeight: 500, wordBreak: 'break-all' }}>{String(value)}</span>
                                        )}
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                {(viewModal.data.documents || []).length > 0 && (
                  <>
                    <div style={{ marginTop: 16, marginBottom: 8, fontSize: 12, fontWeight: 600, color: '#525b66' }}>Documents</div>
                    {viewModal.data.documents.map((d, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>{d.name}</div>
                        </div>
                        <span className={`ob-badge ${
                          d.status === 'completed' || d.status === 'accepted' ? 'ob-badge--green'
                          : d.status === 'pending' ? 'ob-badge--amber'
                          : d.status === 'rejected' ? 'ob-badge--red'
                          : 'ob-badge--gray'
                        }`} style={{ textTransform: 'capitalize' }}>
                          {d.status}
                        </span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            ) : (
              <div className="ob-empty" style={{ padding: '20px' }}>
                <div className="ob-empty__title">No data available</div>
              </div>
            )}
            {!viewModal.loading && viewModal.data?.run?.status === 'in_review' && canEdit && (
              <div style={{ display: 'flex', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid #e4e9f0' }}>
                <button
                  className="ob-btn"
                  style={{ flex: 1 }}
                  onClick={() => handleRequestChanges(viewModal.runId)}
                >
                  Request Changes
                </button>
                <button
                  className="ob-btn"
                  style={{ flex: 1, background: '#22c55e', color: '#fff', borderColor: '#22c55e' }}
                  onClick={() => handleCompleteReview(viewModal.runId)}
                >
                  Mark as Completed
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveOnboarding;
